"use client";

import { c as _c } from "react/compiler-runtime";
import { MetaDot } from "@/components/ui/meta-dot";
import { IconPencilOutline18, IconTrashOutline18 } from "nucleo-ui-outline-18";
import type { Connector, ConnectorType } from "@/stores/connectorsStore";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { ConnectorLogo } from "./ConnectorLogo";
function truncate(value: string, start: number, end: number): string {
  if (value.length <= start + end + 3) return value;
  return `${value.slice(0, start)}…${value.slice(-end)}`;
}
const ADDRESS_CONNECTOR_TYPES: Record<string, {
  field: string;
  namePrefix: string;
}> = {
  railgun: {
    field: "railgunAddress",
    namePrefix: "RAILGUN Wallet"
  }
};
function getConnectorDisplayName(connector: Connector): string {
  const meta = ADDRESS_CONNECTOR_TYPES[connector.type];
  if (meta) {
    return meta.namePrefix;
  }
  if (connector.name.length > 48) {
    return `${connector.name.slice(0, 42)}…`;
  }
  return connector.name;
}
function getConnectorDisplayId(connector: Connector): string {
  const meta = ADDRESS_CONNECTOR_TYPES[connector.type];
  if (meta) {
    const raw = connector.maskedData?.[meta.field];
    if (typeof raw === "string") {
      return raw.length > 32 ? truncate(raw, 16, 8) : raw;
    }
  }
  return `ID: ${connector.id}`;
}
interface ConfiguredConnectorRowProps {
  connector: Connector;
  connectorType?: ConnectorType;
  isDeleting: boolean;
  onEdit: (connector: Connector) => void;
  onDelete: (id: string) => void;
  hideTypeBadge?: boolean;
  variant?: "card" | "plain";
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}
export function ConfiguredConnectorRow(t0) {
  const $ = _c(70);
  const {
    connector,
    connectorType,
    isDeleting,
    onEdit,
    onDelete,
    hideTypeBadge: t1,
    variant: t2,
    isFirstInGroup: t3,
    isLastInGroup: t4
  } = t0;
  const hideTypeBadge = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "card" : t2;
  const isFirstInGroup = t3 === undefined ? false : t3;
  const isLastInGroup = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== connector) {
    t5 = getConnectorDisplayName(connector);
    $[0] = connector;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const displayName = t5;
  let t6;
  if ($[2] !== connector) {
    t6 = getConnectorDisplayId(connector);
    $[2] = connector;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const displayId = t6;
  const isPlain = variant === "plain";
  const t7 = isPlain ? "relative py-4 pl-12 md:pl-14" : "ds-shell p-5 transition-[border-color,box-shadow,transform] duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] hover:shadow-[0_12px_30px_-18px_rgba(15,23,42,0.28)] active:scale-[0.997]";
  let t8;
  if ($[4] !== isFirstInGroup || $[5] !== isLastInGroup || $[6] !== isPlain) {
    t8 = isPlain && <>{!isFirstInGroup && <div className="border-ds-neutral-300 dark:border-ds-neutral-700 absolute -top-4 bottom-1/2 left-4 w-0 border-l-2 border-dashed" />}<div className="rounded-bl-ds-sm border-ds-neutral-300 dark:border-ds-neutral-700 absolute left-4 top-1/2 z-[1] h-3 w-5 -translate-y-full border-b-2 border-l-2" />{!isLastInGroup && <div className="border-ds-neutral-300 dark:border-ds-neutral-700 absolute bottom-[-1rem] left-4 top-1/2 w-0 border-l-2 border-dashed" />}</>;
    $[4] = isFirstInGroup;
    $[5] = isLastInGroup;
    $[6] = isPlain;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = isPlain ? "" : "ds-logo-wrapper";
  const t10 = connectorType?.logoUrl;
  const t11 = isPlain ? "base" : "md";
  let t12;
  if ($[8] !== connector.name || $[9] !== connector.type || $[10] !== t10 || $[11] !== t11) {
    t12 = <ConnectorLogo type={connector.type} name={connector.name} logoUrl={t10} size={t11} />;
    $[8] = connector.name;
    $[9] = connector.type;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t12 || $[14] !== t9) {
    t13 = <div className={t9}>{t12}</div>;
    $[13] = t12;
    $[14] = t9;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== connector.name || $[17] !== displayName) {
    t14 = <h3 className="text-ds-text-secondary min-w-0 truncate font-mono text-sm font-medium uppercase" title={connector.name}>{displayName}</h3>;
    $[16] = connector.name;
    $[17] = displayName;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== connector.type || $[20] !== connectorType?.name || $[21] !== hideTypeBadge) {
    t15 = !hideTypeBadge && <span className="border-ds-border bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled flex-shrink-0 rounded-md border px-1.5 py-0.5 text-xs font-medium">{connectorType?.name || connector.type}</span>;
    $[19] = connector.type;
    $[20] = connectorType?.name;
    $[21] = hideTypeBadge;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t14 || $[24] !== t15) {
    t16 = <div className="mb-0.5 flex min-w-0 items-center gap-3">{t14}{t15}</div>;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== connector.description) {
    t17 = connector.description && <p className="text-ds-text-muted line-clamp-1 text-sm">{connector.description}</p>;
    $[26] = connector.description;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== displayId) {
    t18 = <span className="truncate" title={displayId}>{displayId}</span>;
    $[28] = displayId;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <MetaDot />;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== connector.createdAt) {
    t20 = new Date(connector.createdAt).toLocaleDateString();
    $[31] = connector.createdAt;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t20) {
    t21 = <span className="flex-shrink-0">Created: {t20}</span>;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t18 || $[36] !== t21) {
    t22 = <div className="text-ds-text-disabled dark:text-ds-text-muted mt-0.5 flex items-center gap-1.5 text-xs">{t18}{t19}{t21}</div>;
    $[35] = t18;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t16 || $[39] !== t17 || $[40] !== t22) {
    t23 = <div className="min-w-0 flex-1">{t16}{t17}{t22}</div>;
    $[38] = t16;
    $[39] = t17;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== connector || $[43] !== onEdit) {
    t24 = () => onEdit(connector);
    $[42] = connector;
    $[43] = onEdit;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  const t25 = `flex size-9 items-center justify-center rounded-xl ${isPlain ? "border border-transparent" : "border-ds-border border"} text-ds-text-muted hover:bg-ds-neutral-100 hover:text-ds-text-secondary dark:text-ds-text-disabled dark:hover:bg-ds-neutral-800 transition-[transform,background-color,border-color,color] duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] dark:hover:text-zinc-200`;
  let t26;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <IconPencilOutline18 className="size-4" />;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== t24 || $[47] !== t25) {
    t27 = <button onClick={t24} className={t25} title="Edit connector">{t26}</button>;
    $[46] = t24;
    $[47] = t25;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== connector.id || $[50] !== onDelete) {
    t28 = () => onDelete(connector.id);
    $[49] = connector.id;
    $[50] = onDelete;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  const t29 = `flex size-9 items-center justify-center rounded-xl ${isPlain ? "border border-transparent" : "border border-red-200 dark:border-red-900/30"} text-red-600 transition-[transform,background-color,border-color,color,opacity] duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] hover:bg-red-50 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20`;
  let t30;
  if ($[52] !== isDeleting) {
    t30 = isDeleting ? <PixelLoader size="sm" /> : <IconTrashOutline18 className="size-4" />;
    $[52] = isDeleting;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== isDeleting || $[55] !== t28 || $[56] !== t29 || $[57] !== t30) {
    t31 = <button onClick={t28} disabled={isDeleting} className={t29} title="Delete connector">{t30}</button>;
    $[54] = isDeleting;
    $[55] = t28;
    $[56] = t29;
    $[57] = t30;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== t27 || $[60] !== t31) {
    t32 = <div className="flex gap-2">{t27}{t31}</div>;
    $[59] = t27;
    $[60] = t31;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== t13 || $[63] !== t23 || $[64] !== t32) {
    t33 = <div className="flex items-center gap-4">{t13}{t23}{t32}</div>;
    $[62] = t13;
    $[63] = t23;
    $[64] = t32;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== t33 || $[67] !== t7 || $[68] !== t8) {
    t34 = <div className={t7} data-guide="connector-row">{t8}{t33}</div>;
    $[66] = t33;
    $[67] = t7;
    $[68] = t8;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  return t34;
}